import {ColorSchemeType} from 'platform/components';
import {ThemeIconKey} from 'platform/foundation';
import {match} from 'ts-pattern';

import {always, filter, groupBy, indexBy, isNil, map, pipe, sort, toPairs} from 'ramda';

import {
  DefinitionTemplateListResponseBody,
  DefinitionWithTemplatesResponseBody,
  DocumentGroupEnum,
  DocumentGroupListResponseBody,
  DocumentGroupResponseBody,
  TemplateResponseBody,
} from '@dms/api/core';
import i18n from '@dms/i18n';

type Category = {
  label: string;
  icon: ThemeIconKey;
  position: number;
  id: string;
  colorStripe: ColorSchemeType;
};

const TEMPLATE_CATEGORIES: Record<string, Category> = {
  sales: {
    id: 'sales',
    label: i18n.t('general.labels.sell'),
    icon: 'action/assessment',
    colorStripe: 'blue',
    position: 1,
  },
  afterSales: {
    id: 'afterSales',
    label: i18n.t('general.labels.service'),
    icon: 'action/build',
    colorStripe: 'purple',
    position: 3,
  },
  accounting: {
    id: 'accounting',
    label: i18n.t('module.accounting.title'),
    icon: 'custom/invoice',
    position: 2,
    colorStripe: 'orange',
  },
  caraudit: {
    id: 'caraudit',
    label: i18n.t('module.CarAudit.title'),
    icon: 'maps/directions_car',
    position: 4,
    colorStripe: 'teal',
  },
  other: {
    id: 'other',
    label: i18n.t('entity.vehicle.labels.otherLabel'),
    icon: 'AV/replay',
    position: 5,
    colorStripe: 'neutral',
  },
};

const CATEGORY_BY_DOCUMENT_GROUP: Record<DocumentGroupEnum, Category> = {
  business_case: TEMPLATE_CATEGORIES.sales,
  vehicle: TEMPLATE_CATEGORIES.sales,
  vehicle_warehouse: TEMPLATE_CATEGORIES.sales,
  warehouse: TEMPLATE_CATEGORIES.afterSales,
  workshop: TEMPLATE_CATEGORIES.afterSales,
  pneu_hotel: TEMPLATE_CATEGORIES.afterSales,
  cash_register_document: TEMPLATE_CATEGORIES.accounting,
  accounting: TEMPLATE_CATEGORIES.accounting,
  car_audit: TEMPLATE_CATEGORIES.caraudit,
};

const isDocumentGroupEnabled = (
  definition: DefinitionWithTemplatesResponseBody,
  hasSalesLicense: boolean,
  canReadPneuhotel: boolean,
  canReadArticle: boolean,
  canReadServiceCase: boolean
) =>
  match(definition.group)
    .with('warehouse', () => canReadArticle)
    .with('workshop', () => canReadServiceCase)
    .with('pneu_hotel', () => canReadPneuhotel)
    .with('business_case', () => hasSalesLicense)
    .with('vehicle_warehouse', () => hasSalesLicense)
    .otherwise(always(true));

const groupDefinitionByCategory = (definition: DefinitionWithTemplatesResponseBody) =>
  CATEGORY_BY_DOCUMENT_GROUP[definition.group]?.id ?? 'unknown-category';

const groupByGroupId = (definition: DefinitionWithTemplatesResponseBody) =>
  definition.group as string;

const sortByPosition = <A extends {category: Category}, B extends {category: Category}>(
  a: A,
  b: B
) => a.category.position - b.category.position;

const sortByIsPrimary = <A extends {isPrimary: boolean}, B extends {isPrimary: boolean}>(
  a: A,
  b: B
) => {
  const aPrimary = a.isPrimary ? 1 : 0;
  const bPrimary = b.isPrimary ? 1 : 0;

  return bPrimary - aPrimary;
};

const addGroupToDefinitions =
  (groupsById: Record<DocumentGroupEnum, DocumentGroupResponseBody>) =>
  ([group, definitions]: [string, DefinitionWithTemplatesResponseBody[] | undefined]) => ({
    group: {
      id: group,
      title: groupsById[group as DocumentGroupEnum]?.title ?? '',
    },
    definitions: definitions ?? [],
  });

const addIsPrimaryAttribute =
  (primaryTemplatesIds: Set<string>) => (template: TemplateResponseBody) => ({
    ...template,
    isPrimary: primaryTemplatesIds.has(template.id),
  });

export const createDocumentTemplatesStructure = (
  definitionTemplateList: DefinitionTemplateListResponseBody | undefined,
  documentGroupList: DocumentGroupListResponseBody | undefined,
  hasSalesLicense: boolean,
  canReadPneuhotel: boolean,
  canReadArticle: boolean,
  canReadServiceCase: boolean
) => {
  if (isNil(definitionTemplateList) || isNil(documentGroupList)) {
    return [];
  }

  const groupsById = indexBy((group) => group.key, documentGroupList.groups ?? []);

  const primaryTemplatesIds = new Set<string>();
  definitionTemplateList.definitions.forEach((definition) => {
    primaryTemplatesIds.add(definition.primaryTemplateId);
  });

  const isDefinitionEnabled = (definition: DefinitionWithTemplatesResponseBody) => {
    if (definition.documentCode === 'gdpr-marketing-consent') {
      return false;
    }

    return isDocumentGroupEnabled(
      definition,
      hasSalesLicense,
      canReadPneuhotel,
      canReadArticle,
      canReadServiceCase
    );
  };

  return pipe(
    filter(isDefinitionEnabled),
    groupBy(groupDefinitionByCategory),
    toPairs,
    map(([categoryId, definitions]) => ({
      category: TEMPLATE_CATEGORIES[categoryId],
      groups: pipe(
        groupBy(groupByGroupId),
        toPairs,
        map(addGroupToDefinitions(groupsById)),
        map((item) => ({
          group: item.group,
          definitions: item.definitions.map((definition) => ({
            ...definition,
            templates: definition.templates
              .filter((template) => isNil(template.authorizationProfileId))
              .map((template) => ({
                ...addIsPrimaryAttribute(primaryTemplatesIds)(template),
                definitionType: definition.type,
              }))
              .sort(sortByIsPrimary),
          })),
        }))
      )(definitions ?? []),
    })),
    sort(sortByPosition)
  )(definitionTemplateList.definitions);
};
